import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import {
  NAME,
  PRIORITY,
  START_DATE,
  END_DATE,
  TASK_STATUS,
  TASK_ATTACHMENT,
  TASKS,
  NOTES,
  ASSIGNEES,
  SCHOOL,
  ROLE,
  ASSIGN_TO,
  TASK_ATTACHMENT_NAME,
} from "../consts";
import { routes } from "@/routes/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { formValues, schema } from "./validations";
import {
  useCreateTaskAssignmentMutation,
  useCreatePredefinedTaskAssignmentMutation,
  useGetTaskAssignmentByIdQuery,
  useUpdateTaskAssignmentMutation,
} from "@/services";
import { useAppDispatch } from "@/redux/hooks";
import { openSnackbarAction } from "@/redux/slices";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useGetCrmResourceManagementQuery } from "@/services/school"; // Adjust import path as needed
import { LOGIN_ROLES } from "@/lib/utils";

const useAddEdit = () => {
  const {
    action,
    id: taskId,
    schoolId: urlSchoolId,
  } = useParams<{
    action: string;
    id?: string;
    schoolId?: string;
  }>();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const attachmentRef = useRef(null);

  const isView = action === routes.view;
  const isEdit = action === routes.edit;
  const location = useLocation();

  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  // 👇 backend sends role as array, e.g. ["CRM_HEAD"]
  const roles = loggedInUserData?.role || [];

  // Helper to check if user has a given role
  const hasRole = (role: string) =>
    Array.isArray(roles) && roles.includes(role);
  const isLabsTeamHead =
    hasRole(LOGIN_ROLES.LABS_TEAM_HEAD) || hasRole(LOGIN_ROLES.SUPER_ADMIN);

  const extractSchoolIdFromPath = () => {
    const pathSegments = location.pathname.split("/");

    // URL structure: /crm-head/school/{schoolId}/{action}/school-task-assignment/...
    const schoolIndex =
      pathSegments.findIndex((segment) => segment === "school") + 1;

    if (schoolIndex > 0 && schoolIndex < pathSegments.length) {
      const potentialSchoolId = pathSegments[schoolIndex];
      // Only return if it's not a reserved action word
      if (!["add", "edit", "view"].includes(potentialSchoolId)) {
        return potentialSchoolId;
      }
    }

    return "";
  };

  // Extract school ID from URL - this is "xpsschool" in your example
  const schoolId = urlSchoolId || extractSchoolIdFromPath();

  const [taskType, setTaskType] = useState<"new" | "predefined">("new");
  const [selectedPredefinedTasks, setSelectedPredefinedTasks] = useState<any[]>(
    []
  );

  // -----------------------------------
  // Fetch CRM Resource Management Data
  // -----------------------------------
  const { data: crmResourceData, isLoading: isCrmLoading } =
    useGetCrmResourceManagementQuery(schoolId, {
      enabled: !!schoolId, // Only fetch if schoolId is available
    });

  // -----------------------------------
  // Prepare AssignTo options and role mapping
  // -----------------------------------
  const assignToOptions = React.useMemo(() => {
    if (!crmResourceData?.data) return [];

    const options = [];

    // Add relationship managers
    if (crmResourceData.data.relationshipManagerData) {
      options.push(
        ...crmResourceData.data.relationshipManagerData.map((manager: any) => ({
          value: manager.profile_id,
          label: manager.name,
          roles: manager.role || [],
          type: "relationshipManager",
        }))
      );
    }

    // Add region heads
    if (crmResourceData.data.regionHeadData) {
      options.push(
        ...crmResourceData.data.regionHeadData.map((head: any) => ({
          value: head.profile_id,
          label: head.name,
          roles: head.role || [],
          type: "regionHead",
        }))
      );
    }

    return options;
  }, [crmResourceData]);

  // -----------------------------------
  // Initialize form
  // -----------------------------------
  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      taskType: taskType,
      selectedCategory: "",
      [TASKS]: [
        {
          [NAME]: "",
          [PRIORITY]: "",
          [START_DATE]: "",
          [END_DATE]: "",
          [TASK_STATUS]: "",
          [TASK_ATTACHMENT]: "",
          [NOTES]: "",
          [ASSIGNEES]: [
            {
              [SCHOOL]: schoolId,
              [ROLE]: isLabsTeamHead ? LOGIN_ROLES.LABS_TEAM : "",
              [ASSIGN_TO]: "",
            },
          ],
        },
      ],
    },
  });

  const handleTaskTypeChange = (type: "new" | "predefined") => {
    setTaskType(type);
    form.setValue("taskType", type); // Update form value
  };

  const requiredFields = getRequiredFields(schema);

  // -----------------------------------
  // Handle AssignTo change to auto-populate roles
  // -----------------------------------
  const handleAssignToChange = (
    taskIndex: number,
    assignIndex: number,
    selectedProfileId: string
  ) => {
    const tasks = [...form.getValues(TASKS)];

    // 🧪 If user is LABS_TEAM_HEAD → skip lookup and directly set value
    if (isLabsTeamHead) {
      tasks[taskIndex][ASSIGNEES][assignIndex][ASSIGN_TO] = selectedProfileId;
      tasks[taskIndex][ASSIGNEES][assignIndex][ROLE] = LOGIN_ROLES.LABS_TEAM;
      form.setValue(TASKS, tasks);
      return;
    }

    // 🔍 Normal CRM-based lookup
    const selectedOption = assignToOptions.find(
      (option) => option.value === selectedProfileId
    );
    if (selectedOption) {
      const roles = selectedOption.roles;
      tasks[taskIndex][ASSIGNEES][assignIndex][ROLE] = Array.isArray(roles)
        ? roles[0]
        : roles || selectedOption.type || "";
      form.setValue(TASKS, tasks);
    }
  };

  // -----------------------------------
  // API hooks
  // -----------------------------------
  const { mutateAsync: createTaskAssignment, isPending: isCreating } =
    useCreateTaskAssignmentMutation();

  const { mutateAsync: createPredefinedTaskAssignment } =
    useCreatePredefinedTaskAssignmentMutation();

  const { mutateAsync: updateTaskAssignment, isPending: isUpdating } =
    useUpdateTaskAssignmentMutation();

  // Fetch task details (for Edit/View)
  const { data: taskDetailData, isLoading: isFetching } =
    useGetTaskAssignmentByIdQuery(taskId || "", {
      enabled: isEdit || isView,
    });

  // -----------------------------------
  // Populate form when editing
  // -----------------------------------
  useEffect(() => {
    if (taskDetailData?.data && (isEdit || isView)) {
      const t = taskDetailData.data;

      form.reset({
        taskType: "new", // Default to "new" for edit/view
        selectedCategory: "",
        [TASKS]: [
          {
            [NAME]: t.taskName,
            [PRIORITY]: t.priority,
            [START_DATE]: t.startDate?.split("T")[0],
            [END_DATE]: t.endDate?.split("T")[0],
            [TASK_STATUS]: t.taskStatus,
            [TASK_ATTACHMENT]: "",
            [TASK_ATTACHMENT_NAME]: t.taskAttachmentName || "",
            [NOTES]: t.taskNotes?.[0]?.note || "",
            [ASSIGNEES]: [
              {
                [SCHOOL]: schoolId,
                [ROLE]: t.role,
                [ASSIGN_TO]: t.assignTo,
              },
            ],
          },
        ],
      });
    }
  }, [taskDetailData, isEdit, isView, form, schoolId]);

  // -----------------------------------
  // Navigation & UI Handlers
  // -----------------------------------

  const onBackClick = () => {
    const state = (location.state || {}) as any;
    const background = state?.background;
    if (background) {
      if (typeof background === "string") {
        navigate(background);
      } else if (background?.pathname) {
        navigate(background.pathname + (background.search || ""));
      } else {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  const onSelectPredefinedTask = (task: any) => {
    setSelectedPredefinedTasks((prev) => {
      if (prev.some((p) => p._id === task._id)) {
        return prev.filter((p) => p._id !== task._id);
      } else {
        return [...prev, task];
      }
    });
  };

  const onRemovePredefinedTask = (task: any) => {
    setSelectedPredefinedTasks((prev) =>
      prev.filter((p) => p._id !== task._id)
    );
  };

  // -----------------------------------
  // Submit Handler
  // -----------------------------------
  const onSubmit = async (data: formValues) => {
    console.log("🚀 onSubmit Triggered", { taskType, data });

    try {
      const cleanedData = setEmptyFieldsToNull(removeEmptyFields(data));
      const firstTask = cleanedData[TASKS]?.[0] || {};

      // Build assignments with proper role lookup
      const baseAssignments =
        firstTask[ASSIGNEES]?.map((assignee: any) => {
          // 🧪 If user is LABS_TEAM_HEAD → directly use LABS_TEAM role
          if (isLabsTeamHead) {
            return {
              school: assignee[SCHOOL] || schoolId,
              role: LOGIN_ROLES.LABS_TEAM,
              assignTo: assignee[ASSIGN_TO],
            };
          }

          const selectedProfileId = assignee[ASSIGN_TO];
          const selectedOption = assignToOptions.find(
            (option) => option.value === selectedProfileId
          );

          const role = selectedOption
            ? Array.isArray(selectedOption.roles)
              ? selectedOption.roles[0]
              : selectedOption.roles
            : assignee[ROLE];

          return {
            school: assignee[SCHOOL] || schoolId,
            role,
            assignTo: assignee[ASSIGN_TO],
          };
        }) || [];

      console.log("🧩 Base Assignments with Roles:", baseAssignments);

      // 🧩 EDIT existing task
      if (isEdit && taskId) {
        const firstAssignee = baseAssignments[0] || {};
        const payload = {
          id: taskId,
          taskName: firstTask[NAME],
          priority: firstTask[PRIORITY],
          taskStatus: firstTask[TASK_STATUS],
          taskAttachment: firstTask[TASK_ATTACHMENT],
          taskAttachmentName: firstTask[TASK_ATTACHMENT_NAME],
          taskNotes: firstTask[NOTES] || "",
          role: firstAssignee.role,
          assignTo: firstAssignee.assignTo,
        };
        console.log("🛠 Update Payload:", payload);
        await updateTaskAssignment(payload);
      }
      // 🆕 Create new task
      else if (data.taskType === "new") {
        const payload = {
          taskName: firstTask[NAME],
          priority: firstTask[PRIORITY],
          startDate: firstTask[START_DATE],
          endDate: firstTask[END_DATE],
          taskStatus: firstTask[TASK_STATUS],
          taskAttachment: firstTask[TASK_ATTACHMENT],
          taskAttachmentName: firstTask[TASK_ATTACHMENT_NAME],
          taskNotes: firstTask[NOTES] || "",
          assignments: baseAssignments,
        };

        console.log("📦 Create Payload (New):", payload);
        await createTaskAssignment(payload);
      }
      // 🧩 Create predefined task(s)
      else if (data.taskType === "predefined") {
        if (!selectedPredefinedTasks.length) {
          const errorMessage = "Please select at least one predefined task";
          dispatch(
            openSnackbarAction({
              message: "Please select at least one predefined task",
              severity: "error",
            })
          );
          toast.error(errorMessage);
          return;
        }

        const tasksPayload = selectedPredefinedTasks.map((item: any) => ({
          taskName: item.taskName,
          priority: item.priority,
          taskNotes: item.taskDescription ? item.taskDescription : "",
          startDate: firstTask[START_DATE],
          endDate: firstTask[END_DATE],
          taskStatus: firstTask[TASK_STATUS],
          taskAttachment: firstTask[TASK_ATTACHMENT],
          taskAttachmentName: firstTask[TASK_ATTACHMENT_NAME],
          assignments: baseAssignments,
        }));

        console.log("📦 Predefined Payload:", tasksPayload);
        await createPredefinedTaskAssignment(tasksPayload);
      }

      dispatch(
        openSnackbarAction({
          message: isEdit
            ? "Task updated successfully!"
            : "Task(s) created successfully!",
          severity: "success",
        })
      );

      // Invalidate and refetch school task assignment queries to immediately update the table
      queryClient.invalidateQueries({ queryKey: ["schoolTaskAssignments"] });
      await queryClient.refetchQueries({ queryKey: ["schoolTaskAssignments"] });
      onBackClick();
    } catch (error: any) {
      console.error("❌ Error submitting task:", error);
      console.error("❌ Error response:", error?.response);
      console.error("❌ Error data:", error?.response?.data);

      // Extract error message from various possible locations
      let errorMessage = "An error occurred while saving the task"; // fallback

      if (error?.response?.data) {
        const errorData = error.response.data;

        // Handle array of validation errors
        if (Array.isArray(errorData)) {
          errorMessage = errorData
            .map((err: any) => err.message || err)
            .join(", ");
        }
        errorMessage =
          errorData.issues.message ||
          errorMessage ||
          errorData.message ||
          errorData.error ||
          errorData;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // Show error in both snackbar and toast for better visibility
      dispatch(
        openSnackbarAction({
          message: errorMessage,
          severity: "error",
        })
      );
      toast.error(errorMessage);
    }
  };

  // -----------------------------------
  // Add/Remove Assignee Handlers
  // -----------------------------------
  const onAddMoreClick = (taskIndex: number) => {
    const tasks = [...form.getValues(TASKS)];
    tasks[taskIndex][ASSIGNEES].push({
      [SCHOOL]: schoolId, // Pre-populate with school ID
      [ROLE]: "",
      [ASSIGN_TO]: "",
    });
    form.setValue(TASKS, tasks);
  };
  const onRemoveAssignField = (taskIndex: number, assignIndex: number) => {
    const currentAssignees = form.getValues(
      `${TASKS}.${taskIndex}.${ASSIGNEES}`
    );
    const updatedAssignees = currentAssignees.filter(
      (_: any, idx: number) => idx !== assignIndex
    );
    form.setValue(`${TASKS}.${taskIndex}.${ASSIGNEES}`, updatedAssignees);
  };

  const onRemoveTask = (taskIndex: number, assignIndex: number) => {
    const tasks = [...form.getValues(TASKS)];
    tasks[taskIndex][ASSIGNEES].splice(assignIndex, 1);
    form.setValue(TASKS, tasks);
  };

  // -----------------------------------
  // Return all handlers and state
  // -----------------------------------
  return {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel: onBackClick,
    isMobile,
    action,
    isEdit,
    attachmentRef,
    onAddMoreClick,
    onRemoveTask,
    isLoading: isCreating || isUpdating || isFetching || isCrmLoading,
    taskType,
    setTaskType: handleTaskTypeChange,
    selectedPredefinedTasks,
    onSelectPredefinedTask,
    onRemovePredefinedTask,
    onRemoveAssignField,
    // New exports for AssignTo functionality
    assignToOptions,
    handleAssignToChange,
    handleTaskTypeChange,
    schoolId,
    crmResourceData,
  };
};

export default useAddEdit;
